import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Package } from "lucide-react";
import { playOpen, playReveal, playTick } from "../lib/sounds";
import { api } from "../context/AuthContext";

// Rarity defaults (common is GREEN). Config from backend overrides color + theme.
const RARITY_DEFAULTS = {
  common:    { color: "#22c55e", label: "Common",    theme: "neon" },
  rare:      { color: "#60A5FA", label: "Rare",      theme: "neon" },
  epic:      { color: "#a855f7", label: "Epic",      theme: "neon" },
  legendary: { color: "#FFD166", label: "Legendary", theme: "arcade" },
};

const ITEM_W = 140;
const ITEM_GAP = 10;
const STRIDE = ITEM_W + ITEM_GAP;
const TOTAL_ITEMS = 60;
const WINNER_INDEX = 54;

function fmt(v) {
  const n = Number(v || 0);
  if (n <= 0) return "Empty";
  return `$${n.toFixed(2)}`;
}

function buildReel(realCrate, cfgs) {
  const rarities = (cfgs && cfgs.length ? cfgs : [
    { rarity: "common", min_reward: 0, max_reward: 1 },
    { rarity: "rare", min_reward: 1, max_reward: 10 },
    { rarity: "epic", min_reward: 10, max_reward: 50 },
    { rarity: "legendary", min_reward: 50, max_reward: 250 },
  ]);
  const items = [];
  for (let i = 0; i < TOTAL_ITEMS; i++) {
    if (i === WINNER_INDEX) {
      items.push({ rarity: realCrate.rarity, reward_amount: realCrate.reward_amount, real: true });
      continue;
    }
    const roll = Math.random();
    const cfg = roll < 0.55 ? rarities[0] : roll < 0.85 ? rarities[1] : roll < 0.97 ? rarities[2] : rarities[3];
    const min = cfg.min_reward, max = cfg.max_reward;
    const amt = Math.round((min + Math.random() * (max - min)) * 100) / 100;
    items.push({ rarity: cfg.rarity, reward_amount: amt });
  }
  return items;
}

const THEMES = {
  neon: {
    label: "neon",
    bg: "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(0,0,0,0.6))",
    overlay: null,
    fontClass: "",
  },
  gothic: {
    label: "gothic",
    bg: "linear-gradient(180deg, rgba(180,20,40,0.10), rgba(0,0,0,0.75))",
    overlay: "radial-gradient(circle at 50% 0%, rgba(255,60,80,0.15), transparent 60%)",
    fontClass: "font-serif italic",
  },
  arcade: {
    label: "arcade",
    bg: "linear-gradient(180deg, rgba(255,209,102,0.10), rgba(0,0,0,0.65))",
    overlay: "repeating-linear-gradient(90deg, rgba(255,209,102,0.05) 0 6px, transparent 6px 12px)",
    fontClass: "tracking-widest",
  },
};

export default function LootReveal({ crate, onClose }) {
  const [phase, setPhase] = useState("idle");
  const [cfgs, setCfgs] = useState([]);
  const [translate, setTranslate] = useState(0);
  const [viewportW, setViewportW] = useState(900);
  const containerRef = useRef(null);
  const tickTimers = useRef([]);
  const startedRef = useRef(false);

  useEffect(() => {
    api.get("/crates/config").then((r) => setCfgs(r.data.config || [])).catch(() => setCfgs([]));
  }, []);

  const reel = useMemo(() => buildReel(crate || { rarity: "common", reward_amount: 0 }, cfgs), [crate, cfgs]);

  const rarityMap = useMemo(() => {
    const map = { ...RARITY_DEFAULTS };
    cfgs.forEach((c) => {
      if (!map[c.rarity]) map[c.rarity] = { ...RARITY_DEFAULTS.common };
      if (c.color) map[c.rarity].color = c.color;
      if (c.reveal_theme) map[c.rarity].theme = c.reveal_theme;
      map[c.rarity].label = c.rarity.charAt(0).toUpperCase() + c.rarity.slice(1);
    });
    return map;
  }, [cfgs]);

  useEffect(() => {
    const onResize = () => { if (containerRef.current) setViewportW(containerRef.current.clientWidth); };
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Kick off the spin exactly once, when the config is ready.
  // We intentionally do NOT depend on viewportW here — the winner's target is
  // -WINNER_INDEX * STRIDE regardless of the viewport width (padding centres it).
  useEffect(() => {
    if (startedRef.current) return;
    if (!crate || cfgs.length === 0) return;
    startedRef.current = true;

    playOpen();

    const target = WINNER_INDEX * STRIDE; // dead-centre landing on the real crate item
    const DURATION = 5200;

    // Schedule tick sounds along the ease-out curve
    for (let i = 1; i <= WINNER_INDEX; i++) {
      const p = Math.min(1, i / WINNER_INDEX);
      const t = 1 - Math.pow(1 - p, 1 / 3);
      const ms = Math.floor(t * DURATION);
      const strength = i > WINNER_INDEX - 6 ? 1.4 : 1;
      tickTimers.current.push(setTimeout(() => playTick(strength), ms));
    }

    requestAnimationFrame(() => {
      setPhase("spinning");
      setTranslate(-target);
    });

    tickTimers.current.push(
      setTimeout(() => {
        setPhase("settled");
        playReveal(crate.rarity || "common");
      }, DURATION + 80)
    );

    return () => {
      // do NOT clear timers here — modal stays open until user closes it
    };
  }, [crate, cfgs]);

  // On unmount only, clear any pending timers.
  useEffect(() => () => {
    tickTimers.current.forEach(clearTimeout);
    tickTimers.current = [];
  }, []);

  if (!crate) return null;

  const won = rarityMap[crate.rarity] || RARITY_DEFAULTS.common;
  const theme = THEMES[won.theme] || THEMES.neon;
  const winnerLabel = fmt(crate.reward_amount);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(0,0,0,0.75)", backdropFilter: "blur(20px)" }}
         onClick={onClose}>
      <button onClick={onClose} className="absolute top-5 right-5 p-2 rounded-full glass"><X className="w-4 h-4" /></button>

      <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-center mb-6">
          <div className={`text-[11px] uppercase tracking-[0.35em] text-white/50 ${theme.fontClass}`}>Opening crate &middot; {theme.label}</div>
          <div className={`mt-1 text-2xl md:text-3xl font-bold tracking-tight ${theme.fontClass}`}>Good luck.</div>
        </div>

        <div className="relative">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-32 z-20" style={{ background: "linear-gradient(90deg, rgba(5,6,10,0.95), transparent)" }} />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-32 z-20" style={{ background: "linear-gradient(270deg, rgba(5,6,10,0.95), transparent)" }} />

          {/* Center pointer */}
          <div className="pointer-events-none absolute top-0 bottom-0 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center justify-between">
            <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-t-[14px] border-l-transparent border-r-transparent" style={{ borderTopColor: won.color }} />
            <div className="w-[2px] flex-1" style={{ background: `${won.color}99`, boxShadow: `0 0 20px ${won.color}` }} />
            <div className="w-0 h-0 border-l-[10px] border-r-[10px] border-b-[14px] border-l-transparent border-r-transparent" style={{ borderBottomColor: won.color }} />
          </div>

          <div ref={containerRef} className="relative glass rounded-3xl py-6 overflow-hidden" style={{ background: theme.bg }}>
            {theme.overlay && (
              <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: theme.overlay }} />
            )}
            <div
              className="flex will-change-transform"
              style={{
                gap: `${ITEM_GAP}px`,
                paddingLeft: `${viewportW / 2 - ITEM_W / 2}px`,
                paddingRight: `${viewportW / 2 - ITEM_W / 2}px`,
                transform: `translate3d(${translate}px, 0, 0)`,
                transition: phase === "spinning" ? "transform 5200ms cubic-bezier(0.05, 0.7, 0.1, 1)" : "none",
              }}
            >
              {reel.map((it, i) => (
                <ReelItem key={i} item={it} isWinner={i === WINNER_INDEX && phase === "settled"} rarityMap={rarityMap} />
              ))}
            </div>
          </div>
        </div>

        <div className={`mt-8 text-center transition-opacity duration-500 ${phase === "settled" ? "opacity-100" : "opacity-0"}`}>
          <div className={`text-[11px] uppercase tracking-[0.3em] ${theme.fontClass}`} style={{ color: won.color }}>{won.label} crate</div>
          <div className="mt-1 text-[15px] text-white/60">You got</div>
          <div className={`mt-2 text-6xl md:text-7xl font-black tracking-[-0.03em] text-white ${theme.fontClass}`} style={{ textShadow: `0 0 40px ${won.color}88` }}>
            {winnerLabel}
          </div>
          <div className="mt-2 text-[12px] uppercase tracking-widest text-white/40">
            {winnerLabel === "Empty" ? "Better luck next crate" : `Stake tip · ${crate.delivery_status}`}
          </div>
          <button onClick={onClose} className="mt-6 px-7 py-3 rounded-full bg-white text-black text-[13px] font-semibold hover:bg-white/90">Continue</button>
        </div>
      </div>
    </div>
  );
}

function ReelItem({ item, isWinner, rarityMap }) {
  const r = rarityMap[item.rarity] || RARITY_DEFAULTS.common;
  const label = fmt(item.reward_amount);
  return (
    <div
      className="relative shrink-0 rounded-2xl overflow-hidden flex flex-col items-center justify-center"
      style={{
        width: `${ITEM_W}px`,
        height: `180px`,
        background: `linear-gradient(180deg, ${r.color}22 0%, rgba(255,255,255,0.02) 55%, rgba(255,255,255,0.02) 100%)`,
        border: `1px solid ${r.color}55`,
        boxShadow: isWinner ? `0 0 40px ${r.color}, inset 0 0 40px ${r.color}30` : `inset 0 0 25px ${r.color}18`,
        transform: isWinner ? "scale(1.03)" : "none",
        transition: "transform 300ms ease, box-shadow 300ms ease",
      }}
    >
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: r.color, boxShadow: `0 0 12px ${r.color}` }} />
      <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: `${r.color}12`, border: `1px solid ${r.color}40` }}>
        <Package className="w-8 h-8" style={{ color: r.color, filter: `drop-shadow(0 0 8px ${r.color})` }} />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest" style={{ color: r.color }}>{r.label}</div>
      <div className={`mt-1 text-lg font-black ${label === "Empty" ? "text-white/60" : "text-white"}`}>{label}</div>
    </div>
  );
}
